/** A stage as the designer holds it while editing, before it is saved. */
export type StageDraft = {
  id: number | null;
  /** Stable key for React while a new stage has no id yet. */
  key: string;
  name: string;
  kind: string;
  condition: Record<string, any>;
  /** Hours allowed for this step, or null for no deadline. */
  sla_hours: number | null;
  form_ids: number[];
  workflow_ids: number[];
};

/**
 * What the inspector is currently editing.
 *
 * The designer edits several different kinds of thing, and the previous version
 * gave each its own tab or its own nested card. One selection and one inspector
 * means there is always exactly one answer to "what am I changing?".
 *
 * Statuses are deliberately not here. The lifecycle is a transition matrix that
 * needs a table far wider than an inspector panel, and it is set up once rather
 * than edited constantly, so it opens in its own drawer instead.
 */
export type Selection =
  | { type: "stage"; key: string }
  | { type: "section"; id: number }
  | { type: "workflow"; id: number }
  | null;

/** A field inside a section's schema, as the process API now reports it. */
export type ProcessField = {
  name: string;
  label: string;
  component: string;
  options: Array<{ label: string; value: string }>;
};

/**
 * A field plus where it lives, for the condition picker.
 *
 * The section and stage matter as much as the name: two sections can both have
 * a field called `title`, and a rule that reads a field from a stage the permit
 * has not reached yet can never be true when it is evaluated.
 */
export type FieldRef = ProcessField & {
  sectionId: number;
  sectionTitle: string;
  /** Position of the stage holding this field; -1 when it is in no stage. */
  stageIndex: number;
};

/** Names in the case namespace whose answers are dates rather than numbers or
 *  text — so the condition builder offers a date picker for them. */
const CASE_DATE_FIELDS = [
  "case.start_date",
  "case.end_date",
  "case.created_at",
  "today",
];

/**
 * The case namespace as ordinary fields.
 *
 * `case.start_date`, `requester.email`, `today` — the things a rule can ask
 * about the case itself rather than about an answer somebody typed. The server
 * sends the list (`choices.condition_case_fields`, mirroring
 * `stages.CASE_NAMESPACE`) so the two cannot drift.
 *
 * Shaped as `FieldRef` on purpose: the picker, the validator, "Try it" and the
 * sentence writer then need no idea these are special. `stageIndex: -1` is what
 * makes the "not answered until later" check pass over them — the case has a
 * start date from the moment it exists.
 */
export const caseFieldRefs = (
  caseFields: Array<{ name: string; label: string }>
): FieldRef[] =>
  caseFields.map(({ name, label }) => ({
    name,
    label,
    component: CASE_DATE_FIELDS.includes(name) ? "DatePicker" : "Input",
    options: [],
    sectionId: -1,
    sectionTitle: "The case itself",
    stageIndex: -1,
  }));

/** Something wrong with the draft, found without asking the server. */
export type Problem = {
  /** Errors block publishing; warnings are worth knowing but legal. */
  severity: "error" | "warning";
  /** The English source string, which is also the translation key. */
  message: string;
  /** Values interpolated into it — kept separate so the string stays a key. */
  params?: Record<string, string>;
  /** What to select when someone clicks the problem. */
  target: Selection;
};

export const sameSelection = (a: Selection, b: Selection): boolean => {
  if (!a || !b) return a === b;
  if (a.type !== b.type) return false;
  if (a.type === "stage") return a.key === (b as typeof a).key;
  return (a as { id: number }).id === (b as { id: number }).id;
};
